import React from 'react';
import { useTranslation } from 'react-i18next';

import { Pill, Label } from './ExportPdf.styles';

export type ExportPdfButtonProps = {
  /** Asks for the report: it is built and downloaded as a PDF once the page holds every figure. */
  onExport: () => void;
  disabled?: boolean;
  /** The export is being prepared (announced; the snackbar shows the progress). */
  busy?: boolean;
};

/* A mask reads luminance, not colour: white keeps the tag, black cuts its letters out. */
const MASK_KEEP = '#fff'; // design-allow: mask luminance, not a colour
const MASK_CUT = '#000'; // design-allow: mask luminance, not a colour

/** A PDF document being downloaded: a page with a "PDF" tag (its letters cut out of the tag, so they show whatever
 *  is behind) and an arrow down. Drawn in the text colour. */
const PdfDownloadGlyph = (): React.ReactElement => {
  const mask = `pdf-tag-${React.useId().replace(/:/g, '')}`;
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <defs>
        <mask id={mask}>
          <rect x="1.5" y="11" width="12.5" height="6.5" rx="1.6" fill={MASK_KEEP} />
          <text
            x="7.75"
            y="15.95"
            textAnchor="middle"
            fontFamily="Helvetica, Arial, sans-serif"
            fontWeight={800}
            fontSize="5"
            fill={MASK_CUT}
          >
            PDF
          </text>
        </mask>
      </defs>
      <g fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
        <path d="M4.75 9.5V5A2.25 2.25 0 0 1 7 2.75h7l5.25 5.25v3.5M14 2.75V8h5.25M4.75 18.75v.25A2.25 2.25 0 0 0 7 21.25h5.5" />
        <path d="M17.5 14v7M14.75 18.5l2.75 2.75 2.75-2.75" />
      </g>
      <rect x="1.5" y="11" width="12.5" height="6.5" rx="1.6" fill="currentColor" mask={`url(#${mask})`} />
    </svg>
  );
};

/** The overview card's "Download Report" button (the report as a PDF). */
export const ExportPdfButton = ({
  onExport,
  disabled = false,
  busy = false,
}: ExportPdfButtonProps): React.ReactElement => {
  const { t } = useTranslation();
  return (
    <Pill
      type="button"
      onClick={onExport}
      disabled={disabled}
      aria-busy={busy}
      aria-label={t('Download Report')}
      title={t('Export as PDF')}
    >
      <PdfDownloadGlyph />
      <Label>{t('Download Report')}</Label>
    </Pill>
  );
};

export default ExportPdfButton;
